import { getRouteApi } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PROPERTY_LEVELS } from '@/constants/property-level';
import { cn } from '@/lib/utils';

import { ESTATES_TOOLBAR_ROUTES } from '../constants/routes';
import { QuickFilterChip } from './estates-filter-shared';

const route = getRouteApi(ESTATES_TOOLBAR_ROUTES.ESTATES);

type EstateFilters = {
  searchString?: string;
  propertyType?: 'house' | 'apartment';
  marketType?: 'buy' | 'rent' | 'sell';
  usageType?: 'commercial';
  propertyLevel?: string;
};

type Segment = 'all' | 'estates' | 'projects';

/** Segmented item type switch, "My Estates" toggle and type / market chips of the estates page. */
export const EstatesQuickFilters = () => {
  const { t } = useTranslation();
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const filters = (search.filters ?? {}) as EstateFilters;
  const { propertyType, marketType, usageType, propertyLevel } = filters;
  const filterOwn = Boolean(search.filterOwn);

  const isProjectsActive = propertyLevel === PROPERTY_LEVELS.PROJECT;
  const isMyEstatesActive = filterOwn && propertyLevel === PROPERTY_LEVELS.OBJECT;

  let segment: Segment = 'all';
  if (isProjectsActive) segment = 'projects';
  else if (propertyLevel === PROPERTY_LEVELS.OBJECT) segment = 'estates';

  const selectSegment = useCallback(
    (next: Segment) => {
      // Re-selecting the active segment must not push a duplicate history entry
      if (next === segment && !filterOwn) return;
      navigate({
        search: (prev) => {
          const prevFilters = (prev.filters ?? {}) as EstateFilters;

          if (next === 'all') {
            const { propertyLevel: _ignored, ...restFilters } = prevFilters;
            return { ...prev, filterOwn: false, filters: restFilters };
          }

          if (next === 'estates') {
            return {
              ...prev,
              filterOwn: false,
              filters: { ...prevFilters, propertyLevel: PROPERTY_LEVELS.OBJECT },
            };
          }

          return {
            ...prev,
            filterOwn: false,
            filters: {
              ...prevFilters,
              propertyLevel: PROPERTY_LEVELS.PROJECT,
              propertyType: undefined,
              usageType: undefined,
              marketType: undefined,
            },
          };
        },
      });
    },
    [navigate, segment, filterOwn]
  );

  const toggleMyEstates = useCallback(() => {
    navigate({
      search: (prev) => {
        const prevFilters = (prev.filters ?? {}) as EstateFilters;
        const isActive =
          Boolean(prev.filterOwn) && prevFilters.propertyLevel === PROPERTY_LEVELS.OBJECT;

        // Turning it off goes back to all items, as on develop
        if (isActive) {
          const { propertyLevel: _ignored, ...restFilters } = prevFilters;
          return { ...prev, filterOwn: false, filters: restFilters };
        }

        return {
          ...prev,
          filterOwn: true,
          filters: { ...prevFilters, propertyLevel: PROPERTY_LEVELS.OBJECT },
        };
      },
    });
  }, [navigate]);

  const toggleFilter = useCallback(
    (key: 'propertyType' | 'marketType' | 'usageType', value: string) => {
      navigate({
        search: (prev) => {
          const prevFilters = (prev.filters ?? {}) as EstateFilters;
          const nextFilters = { ...prevFilters };

          if (nextFilters[key] === value) {
            delete nextFilters[key];
          } else {
            nextFilters[key] = value as never;
          }

          const prevKeys = Object.keys(prevFilters);
          const nextKeys = Object.keys(nextFilters);
          const hasSameValues =
            prevKeys.length === nextKeys.length &&
            prevKeys.every(
              (k) => prevFilters[k as keyof EstateFilters] === nextFilters[k as keyof EstateFilters]
            );

          if (hasSameValues) return prev;

          return { ...prev, filters: nextFilters };
        },
      });
    },
    [navigate]
  );

  const segments: { id: Segment; label: string }[] = [
    { id: 'all', label: t('all') },
    { id: 'estates', label: t('estates') },
    { id: 'projects', label: t('projects') },
  ];

  const typeChips: {
    key: 'propertyType' | 'marketType' | 'usageType';
    value: string;
    label: string;
    active: boolean;
  }[] = [
    { key: 'propertyType', value: 'house', label: t('house'), active: propertyType === 'house' },
    {
      key: 'propertyType',
      value: 'apartment',
      label: t('apartment'),
      active: propertyType === 'apartment',
    },
    {
      key: 'usageType',
      value: 'commercial',
      label: t('commercial'),
      active: usageType === 'commercial',
    },
    { key: 'marketType', value: 'buy', label: t('purchase'), active: marketType === 'buy' },
    { key: 'marketType', value: 'rent', label: t('rent'), active: marketType === 'rent' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="radiogroup"
        aria-label={t('estatesItemTypeLabel')}
        className="inline-flex items-center rounded-full border border-border bg-background p-0.5"
      >
        {segments.map((option) => {
          const active = segment === option.id;
          return (
            // biome-ignore lint/a11y/useSemanticElements: styled segmented control, design markup uses buttons
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => selectSegment(option.id)}
              className={cn(
                'rounded-full px-3.5 py-1 text-sm font-medium transition-colors',
                active ? 'bg-foreground text-background' : 'text-foreground hover:bg-muted'
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <QuickFilterChip active={isMyEstatesActive} onClick={toggleMyEstates}>
        {t('myEstates')}
      </QuickFilterChip>
      <div className="mx-1 h-5 w-px self-center bg-border" />
      {typeChips.map((chip) => (
        <QuickFilterChip
          key={`${chip.key}-${chip.value}`}
          active={chip.active && !isProjectsActive}
          disabled={isProjectsActive}
          onClick={() => toggleFilter(chip.key, chip.value)}
        >
          {chip.label}
        </QuickFilterChip>
      ))}
    </div>
  );
};
